/* storybook-check-ignore */
import { ListingWithComputedProperties } from 'declarations/exclusives/listing';
import {
  getIsOOMLS,
  getListingPriceCents,
  getOriginalListingPriceCents,
} from 'helpers/exclusives/listing';
import { Flex, Text, Button, Link, BoxProps } from '@opendoor/bricks/core';
import InfoIcon from './icons/SimpleInfoIcon';
import { ReactNode, useState } from 'react';
import ArrowIcon from './icons/ArrowIcon';
import DiscountIcon from './icons/DiscountIcon';
import LetterIcon from './icons/LetterIcon';
import BackOutIcon from './icons/BackOutIcon';
import CloseIcon from './icons/CloseIcon';
import WhatIsODExclusivesModal from './WhatIsODExclusivesModal';
import YouReResponsibleForFeesModal from './YouReResponsibleForFeesModal';
import { css } from '@emotion/react';
import { BREAKPOINTS_DICT } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';

const BREAKLINE_CSS = css`
  display: none;
  @media (min-width: 560px) and (max-width: 644px) {
    display: inline-block;
  }
  @media (min-width: 1183px) and (max-width: 1430px) {
    display: inline-block;
  }
`;

const EXCLUSIVES_BENEFITS: BenefitRowProps[] = [
  {
    icon: <ArrowIcon size={16} />,
    text: (
      <>
        Purchase priority over <Text as="br" css={BREAKLINE_CSS} />
        other offers
      </>
    ),
  },
  {
    icon: <DiscountIcon size={16} />,
    text: 'Exclusive direct price',
  },
  {
    icon: <LetterIcon size={16} />,
    text: (
      <>
        Appraisal Price Match <Text as="br" css={BREAKLINE_CSS} />
        Guarantee
      </>
    ),
  },
  {
    icon: <BackOutIcon size={16} />,
    text: (
      <>
        Back out anytime, <Text as="br" css={BREAKLINE_CSS} />
        no penalty
      </>
    ),
    hideBorderBottom: true,
  },
];

const NON_EXCLUSIVE: BenefitRowProps[] = [
  {
    icon: <CloseIcon size={16} color="#9FA2B1" />,
    text: (
      <Text
        as="span"
        css={css`
          @media (min-width: 560px) and (max-width: 644px) {
            line-height: calc(2 * var(--lineHeights-s00));
          }
          @media (min-width: 1183px) and (max-width: 1430px) {
            line-height: calc(2 * var(--lineHeights-s00));
          }
        `}
      >
        Exposed to bidding wars
      </Text>
    ),
    isSecondary: true,
  },
  {
    icon: <CloseIcon size={16} color="#9FA2B1" />,
    text: 'Higher MLS list price',
    isSecondary: true,
  },
  {
    icon: <CloseIcon size={16} color="#9FA2B1" />,
    text: (
      <>
        No Appraisal Price Match <Text as="br" css={BREAKLINE_CSS} />
        Guarantee
      </>
    ),
    isSecondary: true,
  },
  {
    icon: <CloseIcon size={16} color="#9FA2B1" />,
    text: 'Earnest money is at risk',
    hideBorderBottom: true,
    isSecondary: true,
  },
];

const MAX_WIDTH_VERTICAL_BP = 1183;

export default function OOMLSBenefits({ listing }: { listing: ListingWithComputedProperties }) {
  const [modal, setModal] = useState<'what-is-od-exclusives' | 'you-re-responsible-for-fees'>();
  const isOOMLS = getIsOOMLS(listing);
  const originalPriceCents = getOriginalListingPriceCents(listing, isOOMLS);
  const priceCents = getListingPriceCents(listing);
  if (!isOOMLS || !originalPriceCents || !priceCents) return null;
  const originalPrice = originalPriceCents / 100;
  const price = priceCents / 100;
  return (
    <>
      <Flex
        flexDirection="row"
        flexWrap="wrap"
        borderBottom="1px solid rgba(0, 0, 0, 0.05)"
        bg="#FCFCFC"
        position="relative"
      >
        <Flex
          bg="neutrals0"
          flexDirection="column"
          width="calc(50% - 0.5px)"
          pl={['1.5rem', '1.5rem', '1.5rem', '3.5rem']}
          pr={['1.5rem', '1.5rem', '1.5rem', '2rem']}
          py="2rem"
          css={css`
            @media screen and (min-width: ${BREAKPOINTS_DICT.MD}px) and (max-width: ${MAX_WIDTH_VERTICAL_BP}px) {
              width: 100% !important;
              padding-bottom: 2rem;
            }
            @media screen and (max-width: 560px) {
              width: 100%;
              padding-bottom: 2rem;
            }
          `}
          paddingBottom="120px"
        >
          <Text color="brand50">Buy it directly on Exclusives</Text>
          <Text
            display="flex"
            as="div"
            color="brand50"
            fontSize="s4"
            fontWeight="semibold"
            opacity="0.8"
            gridGap="0.75rem"
            alignItems="center"
          >
            ${price.toLocaleString()}
          </Text>
          {EXCLUSIVES_BENEFITS.map(BenefitRow)}
        </Flex>
        <Flex
          bg="rgba(0, 0, 0, 0.05)"
          width="1px"
          height="auto"
          css={css`
            @media screen and (min-width: ${BREAKPOINTS_DICT.MD}px) and (max-width: ${MAX_WIDTH_VERTICAL_BP}px) {
              width: 100%;
              height: 1px;
            }
            @media screen and (max-width: 560px) {
              width: 100%;
              height: 1px;
            }
          `}
        />
        <Flex
          flexDirection="column"
          width="calc(50% - 0.5px)"
          css={css`
            @media screen and (max-width: 560px) {
              width: 100%;
            }
            @media screen and (min-width: ${BREAKPOINTS_DICT.MD}px) and (max-width: ${MAX_WIDTH_VERTICAL_BP}px) {
              width: 100%;
              padding-inline-start: 3.5rem;
              padding-inline-end: 2rem;
            }
          `}
          pr={['1.5rem', '1.5rem', '1.5rem', '3.5rem']}
          pl={['1.5rem', '1.5rem', '1.5rem', '2rem']}
          py="2rem"
          paddingBottom="120px"
        >
          <Text style={{ color: '#454647' }}>Buy it everywhere else</Text>
          <Text opacity="0.8" style={{ color: '#333333' }} fontSize="s4">
            ${originalPrice.toLocaleString()}
          </Text>
          {NON_EXCLUSIVE.map(BenefitRow)}
        </Flex>
        <Flex position="absolute" width="100%" bottom="0">
          <Button
            marginBottom="1rem"
            ml={['1.5rem', '1.5rem', 'auto', '3.5rem']}
            mr={['1.5rem', '1.5rem', 'auto', '2rem']}
            position="relative"
            gridGap="0.375rem"
            variant="secondary"
            borderColor="brand50"
            color="brand50"
            height="unset"
            minHeight="48px"
            paddingY="0.75rem"
            width="100%"
            _hover={{ color: 'brand50', bg: 'neutrals30', opacity: 0.8 }}
            paddingX="2.5rem"
            analyticsName=""
            aria-label={null}
            onClick={() => setModal('what-is-od-exclusives')}
            fontSize="s1"
            lineHeight="s000"
            overflow="hidden"
          >
            <Flex minW="1rem" minH="1rem">
              <InfoIcon size="1rem" />
            </Flex>
            Learn more about buying directly on Exclusives
          </Button>
        </Flex>
      </Flex>
      <Text
        style={{ color: '#333333' }}
        opacity="0.8"
        py="1.75rem"
        borderBottom="1px solid rgba(0, 0, 0, 0.05)"
        pl={['1.5rem', '1.5rem', '1.5rem', '3.5rem']}
        pr={['1.5rem', '1.5rem', '1.5rem', '2rem']}
      >
        When you buy directly on Exclusives, there are no agent fees. If you use your own agent,{' '}
        <Link
          aria-label={null}
          analyticsName=""
          fontWeight="semibold"
          onClick={() => setModal('you-re-responsible-for-fees')}
        >
          you’re responsible for their fees
        </Link>
        .
      </Text>
      <WhatIsODExclusivesModal
        isOpen={modal === 'what-is-od-exclusives'}
        onRequestClose={() => setModal(undefined)}
      />
      <YouReResponsibleForFeesModal
        isOpen={modal === 'you-re-responsible-for-fees'}
        onRequestClose={() => setModal(undefined)}
      />
    </>
  );
}

type BenefitRowProps = {
  icon: ReactNode;
  text: ReactNode;
  isSecondary?: boolean;
  hideBorderBottom?: boolean;
};

function BenefitRow({
  icon,
  text,
  isSecondary,
  hideBorderBottom,
  ...props
}: BenefitRowProps & BoxProps) {
  return (
    <Flex
      flexDirection="row"
      gridGap="1rem"
      alignItems="center"
      paddingY="1rem"
      borderBottom={!hideBorderBottom ? '1px solid rgba(0, 0, 0, 0.05)' : undefined}
      {...props}
    >
      {icon}
      <Text
        style={{
          color: !isSecondary ? '#3245A7' : 'rgba(0, 0, 0, 0.7)',
          opacity: isSecondary ? 0.7 : 'unset',
        }}
        fontSize="s0"
        lineHeight="s00"
      >
        {text}
      </Text>
    </Flex>
  );
}
